/**
 * The reader's list context: "1 of 148", and where ‹ › go.
 *
 * ## Why the LIST builds this and the reader does not
 *
 * The reader is opened by a URL. A URL knows one message; it does not know
 * which list that message was sitting in, how that list was filtered, how it
 * was sorted, or how many rows matched. Only the surface the user clicked
 * from knows any of that — so it hands the answer over in router state rather
 * than the reader re-deriving it from whatever page of rows happens to be in
 * the React Query cache. A re-derived position would be confidently wrong the
 * moment the reader is opened from a filtered list, a second tab, or a deep
 * link.
 *
 * ## Absent beats guessed
 *
 * A deep link, a refresh and the personal lane's cache-fallback path all arrive
 * with no state. `ThreadReaderToolbar` renders no counter and disables ‹ › when
 * `position`/`total` are not finite — which is honest. The prototype always
 * draws "1 of 148" because its list is a fixture; ours sometimes genuinely does
 * not know, and inventing a number there is worse than the blank.
 *
 * `total` is the SERVER's match count, not `rows.length` — the list pages, so
 * `rows` is only what has been scrolled into memory so far. `position` is
 * 1-based, as the design writes it.
 */

/**
 * @param rows  the loaded rows, in the order the list displays them
 * @param id    the identity of the row being opened
 * @param total the server's total match count for the current filters
 * @param idOf  how to read a row's identity (comm_id here, conversation there)
 */
export function buildThreadNav(rows, id, total, idOf = (r) => r?.comm_id) {
    if (!Array.isArray(rows) || !id) return null;
    const index = rows.findIndex((row) => idOf(row) === id);
    if (index < 0) return null;
    return {
        position: index + 1,
        // Fall back to what we can see rather than reporting `1 of undefined`.
        total: Number.isFinite(total) && total > 0 ? total : rows.length,
        prevId: index > 0 ? idOf(rows[index - 1]) : null,
        // `null` at the tail even when more pages exist: the next row has not
        // been fetched, so there is no id to navigate to. The control disables,
        // which is the truth — it is not "there is nothing after this".
        nextId: index < rows.length - 1 ? idOf(rows[index + 1]) : null,
    };
}
